<script setup lang="ts">
import { getPrettyCurrentDate } from '@@/bits/date'
import { isAppUsing } from '@@/bits/flip'
import { __ } from '@@/bits/intl'
import { currentUrl, transformCurrentUrl } from '@@/bits/location'
import { PricingPlansExperimentVariant } from '@@/enums'
import OzBox, { OzBoxColors } from '@@/library/v4/components/OzBox.vue'
import OzCloseButton from '@@/library/v4/components/OzCloseButton.vue'
import OzContainedButton, {
  OzContainedButtonColorScheme,
  OzContainedButtonSizePreset,
} from '@@/library/v4/components/OzContainedButton.vue'
import OzDivider, { OzDividerColorScheme } from '@@/library/v4/components/OzDivider.vue'
import OzIndeterminateSpinner from '@@/library/v4/components/OzIndeterminateSpinner.vue'
import OzScreenSizeAwareCenterModal from '@@/library/v4/components/OzScreenSizeAwareCenterModal.vue'
import {
  UpgradeModalCopyAExperimentVariant,
  UpgradeModalSingleStepExperimentVariant,
  useExperimentsStore,
} from '@@/pinia/experiments'
import { PaymentSchedule, Tier, UpgradeSource, UpgradeStep, usePersonalPlansStore } from '@@/pinia/personal_plans_store'
import { useWindowSizeStore } from '@@/pinia/window_size'
import { type User } from '@@/types'
import BillingCollectionItems from '@@/vuecomponents/BillingCollectionItems.vue'
import DashboardSettingsBillingModalTitle from '@@/vuecomponents/DashboardSettingsBillingModalTitle.vue'
import GoldMembershipTier, { Variant as GoldMembershipTierVariant } from '@@/vuecomponents/GoldMembershipTier.vue'
import MembershipTierActionButton from '@@/vuecomponents/MembershipTierActionButton.vue'
import NeonMembershipTier, { Variant as NeonMembershipTierVariant } from '@@/vuecomponents/NeonMembershipTier.vue'
import PaymentScheduleTier from '@@/vuecomponents/PaymentScheduleTier.vue'
import PlatinumMembershipTier, {
  Variant as PlatinumMembershipTierVariant,
} from '@@/vuecomponents/PlatinumMembershipTier.vue'
import SilverMembershipTier, { Variant as SilverMembershipTierVariant } from '@@/vuecomponents/SilverMembershipTier.vue'
import { storeToRefs } from 'pinia'
import { computed, ref } from 'vue'

const props = withDefaults(
  defineProps<{
    user: User
    whiteboardFramesUsed?: number
    fileThatExceededSize?: File
  }>(),
  {
    whiteboardFramesUsed: undefined,
    fileThatExceededSize: undefined,
  },
)

const emit = defineEmits<{
  (e: 'cancel'): void
  (e: 'request-another-free-padlet'): void
}>()

const currentDate = getPrettyCurrentDate()

const { isSmallerThanTabletPortrait, windowHeight } = storeToRefs(useWindowSizeStore())

const personalPlansStore = usePersonalPlansStore()
const {
  displayedEstimatedUpgradeCost,
  displayedPrices,
  displayedQuotas,
  isProcessingUpgrade,
  plans,
  plansFetched,
  advertisedQuotas,
  isLoadingAdvertisedQuotas,
  upgradeStep,
  upgradeError,
  upgradeSource,
  hasUserTriggeredUpgrade,
  estimatedCancellationRefundAmount,
  isLoadingCancellationRefundInformation,
} = storeToRefs(personalPlansStore)
const {
  upgradeToGold,
  upgradeToGoldDifferentCurrency,
  upgradeToPlatinum,
  upgradeToPlatinumDifferentCurrency,
  showPlanUpgradeToGoldModal,
  showUpgradeToPlatinumModalCheckingScheduledChanges,
  handleUpgrade,
} = personalPlansStore

const experimentsStore = useExperimentsStore()
const { upgradeModalCopyAExperimentVariant, upgradeModalSingleStepExperimentVariant } = storeToRefs(experimentsStore)

const xFrameLimitInsteadOfFileLimit = computed(() => upgradeStep.value === UpgradeStep.ChooseTierFrameQuota)

const currentPlanFrameLimit = computed(() => {
  switch (props.user.membership_tier) {
    case 'neon':
      return advertisedQuotas.value.neon.max_whiteboard_frames
    case 'silver':
      return advertisedQuotas.value.silver.max_whiteboard_frames
    case 'gold':
      return advertisedQuotas.value.gold.max_whiteboard_frames
    case 'platinum':
      return advertisedQuotas.value.platinum.max_whiteboard_frames
    default:
      return 0
  }
})

const padletQuotaNeonMembershipTierVariant = computed((): NeonMembershipTierVariant => {
  if (
    xFrameLimitInsteadOfFileLimit.value &&
    props.whiteboardFramesUsed &&
    advertisedQuotas.value.neon.max_whiteboard_frames
  ) {
    if (props.whiteboardFramesUsed > advertisedQuotas.value.neon.max_whiteboard_frames)
      return NeonMembershipTierVariant.OverFrameQuota
    if (props.whiteboardFramesUsed === advertisedQuotas.value.neon.max_whiteboard_frames)
      return NeonMembershipTierVariant.AtFrameQuota
    return NeonMembershipTierVariant.UnderFrameQuota
  }

  if (props.user.quota.walls_used > advertisedQuotas.value.neon.max_walls!)
    return NeonMembershipTierVariant.OverPadletQuota
  if (props.user.quota.walls_used === advertisedQuotas.value.neon.max_walls)
    return NeonMembershipTierVariant.AtPadletQuota
  return NeonMembershipTierVariant.UnderPadletQuota
})

const padletQuotaSilverMembershipTierVariant = computed((): SilverMembershipTierVariant => {
  if (
    xFrameLimitInsteadOfFileLimit.value &&
    props.whiteboardFramesUsed &&
    advertisedQuotas.value.silver.max_whiteboard_frames
  ) {
    if (props.user.membership_tier === 'silver') {
      if (props.whiteboardFramesUsed > advertisedQuotas.value.silver.max_whiteboard_frames)
        return SilverMembershipTierVariant.OverFrameQuota
      if (props.whiteboardFramesUsed === advertisedQuotas.value.silver.max_whiteboard_frames)
        return SilverMembershipTierVariant.AtFrameQuota
      return SilverMembershipTierVariant.UnderFrameQuota
    }

    if (props.whiteboardFramesUsed > advertisedQuotas.value.silver.max_whiteboard_frames) {
      return SilverMembershipTierVariant.OverFrameQuota
    }
    return SilverMembershipTierVariant.Upgrade
  }

  if (props.user.membership_tier === 'silver') {
    if (props.user.quota.walls_used > advertisedQuotas.value.silver.max_walls!)
      return SilverMembershipTierVariant.OverPadletQuota
    if (props.user.quota.walls_used === advertisedQuotas.value.silver.max_walls)
      return SilverMembershipTierVariant.AtPadletQuota
    return SilverMembershipTierVariant.UnderPadletQuota
  }
  if (props.user.quota.walls_used > 10) return SilverMembershipTierVariant.OverPadletQuota
  if (
    xPlatinumMonthlyPrice.value &&
    upgradeModalSingleStepExperimentVariant.value === UpgradeModalSingleStepExperimentVariant.Variation
  )
    return SilverMembershipTierVariant.UpgradeSingleStep
  return SilverMembershipTierVariant.Upgrade
})

const padletQuotaGoldMembershipTierVariant = computed((): GoldMembershipTierVariant => {
  if (
    xFrameLimitInsteadOfFileLimit.value &&
    props.whiteboardFramesUsed &&
    advertisedQuotas.value.gold.max_whiteboard_frames
  ) {
    if (props.user.membership_tier === 'gold') {
      if (props.whiteboardFramesUsed > advertisedQuotas.value.gold.max_whiteboard_frames)
        return GoldMembershipTierVariant.OverFrameQuota
      if (props.whiteboardFramesUsed === advertisedQuotas.value.gold.max_whiteboard_frames)
        return GoldMembershipTierVariant.AtFrameQuota
      return GoldMembershipTierVariant.UnderFrameQuota
    }

    if (props.whiteboardFramesUsed > advertisedQuotas.value.gold.max_whiteboard_frames) {
      return GoldMembershipTierVariant.OverFrameQuota
    }
    return GoldMembershipTierVariant.Upgrade
  }

  if (props.user.membership_tier === 'gold') {
    if (props.user.quota.walls_used > advertisedQuotas.value.gold.max_walls!)
      return GoldMembershipTierVariant.OverPadletQuota
    if (props.user.quota.walls_used === advertisedQuotas.value.gold.max_walls)
      return GoldMembershipTierVariant.AtPadletQuota
    return GoldMembershipTierVariant.UnderPadletQuota
  }
  if (props.user.quota.walls_used > 20) return GoldMembershipTierVariant.OverPadletQuota
  if (
    xPlatinumMonthlyPrice.value &&
    upgradeModalSingleStepExperimentVariant.value === UpgradeModalSingleStepExperimentVariant.Variation
  )
    return GoldMembershipTierVariant.UpgradeSingleStep
  return GoldMembershipTierVariant.Upgrade
})

const platinumMembershipTierVariant = computed((): PlatinumMembershipTierVariant => {
  // We don't need to handle whiteboard because we don't allow the modal to be shown for whiteboards at/over the platinum limit

  if (hasUserTriggeredUpgrade.value) return PlatinumMembershipTierVariant.Loading
  if (
    xPlatinumMonthlyPrice.value &&
    upgradeModalSingleStepExperimentVariant.value === UpgradeModalSingleStepExperimentVariant.Variation &&
    props.user.membership_tier === 'neon'
  )
    return PlatinumMembershipTierVariant.UpgradeSingleStep
  return PlatinumMembershipTierVariant.Upgrade
})

const fileSizeQuotaSilverMembershipTierVariant = computed((): SilverMembershipTierVariant => {
  if (
    props.user.membership_tier === 'silver' ||
    sizeOfFileOverQuota.value > advertisedQuotas.value.silver.max_upload_megabytes! * 1000000
  )
    return SilverMembershipTierVariant.FileSize
  if (
    xPlatinumMonthlyPrice.value &&
    upgradeModalSingleStepExperimentVariant.value === UpgradeModalSingleStepExperimentVariant.Variation
  )
    return SilverMembershipTierVariant.UpgradeSingleStep
  return SilverMembershipTierVariant.Upgrade
})

const fileSizeQuotaGoldMembershipTierVariant = computed((): GoldMembershipTierVariant => {
  if (
    props.user.membership_tier === 'gold' ||
    sizeOfFileOverQuota.value > advertisedQuotas.value.gold.max_upload_megabytes! * 1000000
  )
    return GoldMembershipTierVariant.FileSize
  if (
    xPlatinumMonthlyPrice.value &&
    upgradeModalSingleStepExperimentVariant.value === UpgradeModalSingleStepExperimentVariant.Variation
  )
    return GoldMembershipTierVariant.UpgradeSingleStep
  return GoldMembershipTierVariant.Upgrade
})

const sizeOfFileOverQuota = computed(() => {
  return props.fileThatExceededSize ? props.fileThatExceededSize.size : 0
})

const membershipConfirmationPageUrl = computed((): string => {
  return transformCurrentUrl({}, { path: '/memberships/confirmation', search: { next: currentUrl() } })
})

const chooseTierPadletQuotaTitle = computed((): string => {
  if (
    upgradeSource.value === UpgradeSource.UntrashWallQuota ||
    upgradeSource.value === UpgradeSource.UnarchiveWallQuota
  ) {
    return __("You've reached your padlet quota limit.")
  }

  return __('We see you flying. Maybe it’s time to soar?')
})

const chooseTierPadletQuotaSubtitle = computed((): string => {
  if (upgradeSource.value === UpgradeSource.UntrashWallQuota) {
    return __('Upgrade to restore this padlet.')
  }
  if (upgradeSource.value === UpgradeSource.UnarchiveWallQuota) {
    return __('Upgrade to unarchive this padlet.')
  }
  return __('Upgrade your membership to continue making padlets.')
})

const xGoldMonthlyPrice = computed((): boolean => {
  if (!isAppUsing('personalPricingPlansExperiment')) {
    return true
  }
  // Show gold monthly plan when variant is 'platinum_only' or 'control'
  if (props.user.personal_pricing_plan_variant === PricingPlansExperimentVariant.AnnualOnly) {
    return false
  }
  return true
})

const xPlatinumMonthlyPrice = computed((): boolean => {
  // Show platinum monthly plan when variant is 'platinum_only' or 'control'
  if (
    (isAppUsing('personalPricingPlansExperiment') &&
      props.user.personal_pricing_plan_variant === PricingPlansExperimentVariant.AnnualOnly) ||
    (isAppUsing('personalPricingPlansExperimentV2') &&
      props.user.personal_pricing_plan_variant === PricingPlansExperimentVariant.PlatinumSilverAnnualOnly)
  ) {
    return false
  }
  return true
})

const xGoldPlans = computed((): boolean => {
  // Show gold plans when variant is 'annual_only' or 'control'
  if (
    (isAppUsing('personalPricingPlansExperiment') &&
      props.user.personal_pricing_plan_variant === PricingPlansExperimentVariant.PlatinumOnly) ||
    (isAppUsing('personalPricingPlansExperimentV2') &&
      props.user.personal_pricing_plan_variant === PricingPlansExperimentVariant.PlatinumSilverAnnualOnly)
  ) {
    return false
  }
  return true
})

const xSilverPlans = computed((): boolean => {
  // Show silver plans when variant is 'platinum_silver_annual_only'
  if (!isAppUsing('personalPricingPlansExperimentV2')) {
    return false
  }
  return props.user.personal_pricing_plan_variant === PricingPlansExperimentVariant.PlatinumSilverAnnualOnly
})

// #region video recorder quotas
function formatVideoLengthQuota(seconds: number | null): string {
  if (seconds == null) return ''
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = seconds % 60

  if (minutes > 0 && remainingSeconds > 0) {
    return __('%{minutes} minute %{remainingSeconds} second videos', {
      minutes,
      remainingSeconds,
    })
  } else if (minutes > 0) {
    // Note: as of sept 2024, this should be the only case that gets called, since all max video length quotas can be represented in whole minutes.
    return __('%{minutes} minute videos', {
      minutes,
    })
  } else {
    return __('%{remainingSeconds} second videos', {
      remainingSeconds,
    })
  }
}

const displayedVideoLengthQuotas = computed(() => {
  return {
    neon: formatVideoLengthQuota(advertisedQuotas.value.neon.max_video_length_seconds),
    gold: formatVideoLengthQuota(advertisedQuotas.value.gold.max_video_length_seconds),
    silver: formatVideoLengthQuota(advertisedQuotas.value.silver.max_video_length_seconds),
    platinum: formatVideoLengthQuota(advertisedQuotas.value.platinum.max_video_length_seconds),
  }
})
// #endregion

function initiateUpgradeToGold() {
  if (props.user.membership_tier == null) return

  if (!xGoldMonthlyPrice.value) {
    handleUpgrade({
      paymentSchedule: PaymentSchedule.Annual,
      tier: Tier.Gold,
      postCheckoutRedirectUrl: membershipConfirmationPageUrl.value,
    })
    return
  }

  showPlanUpgradeToGoldModal({ upgradeSource: null })
}

function initiateUpgradeToSilver() {
  handleUpgrade({
    paymentSchedule: PaymentSchedule.Annual,
    tier: Tier.Silver,
    postCheckoutRedirectUrl: membershipConfirmationPageUrl.value,
  })
}

function initiateUpgradeToPlatinum() {
  if (props.user.membership_tier == null) return
  if (!xPlatinumMonthlyPrice.value && props.user.membership_tier === 'neon') {
    handleUpgrade({
      paymentSchedule: PaymentSchedule.Annual,
      tier: Tier.Platinum,
      postCheckoutRedirectUrl: membershipConfirmationPageUrl.value,
    })
    return
  }

  showUpgradeToPlatinumModalCheckingScheduledChanges({
    membershipTier: props.user.membership_tier,
    paymentSchedule: props.user.period ?? undefined,
  })
}

// Every method that confirms an upgrade has the same call signature
// This function handles input validation before calling such a method.
function confirmUpgrade(upgradeFn: (payload: { paymentSchedule: string }) => void) {
  if (props.user.period == null) return

  upgradeFn({ paymentSchedule: props.user.period })
}

const hasRequestedAnotherFreePadlet = ref(false)

const xGetAnotherFreePadletButton = computed((): boolean => {
  if (props.user.tenant_id !== 1) return false
  if (!props.user.quota?.can_make) return false
  if (hasRequestedAnotherFreePadlet.value) return false
  if (
    props.user.membership_tier === 'neon' &&
    (props.user.quota.walls_used === 3 || props.user.quota.walls_used === 4)
  ) {
    return true
  }
  if (
    props.user.membership_tier === 'gold' &&
    (props.user.quota.walls_used === 20 || props.user.quota.walls_used === 21)
  ) {
    return true
  }
  return false
})

function getAnotherFreePadlet(): void {
  hasRequestedAnotherFreePadlet.value = true
  emit('request-another-free-padlet')
}

function getTierSelectionTitleFromExperiment(defaultTitle: string): string {
  if (xFrameLimitInsteadOfFileLimit.value) {
    return __('You’ve hit your limit of %{currentPlanFrameLimit} cards.', {
      currentPlanFrameLimit: currentPlanFrameLimit.value,
    })
  }

  return upgradeModalCopyAExperimentVariant.value === UpgradeModalCopyAExperimentVariant.Control
    ? defaultTitle
    : __('Upgrade and join 3.5 million paying users.')
}

function getTierSelectionSubtitleFromExperiment(defaultTitle: string): string {
  if (xFrameLimitInsteadOfFileLimit.value) {
    return __('Upgrade your membership to add more.')
  }

  return upgradeModalCopyAExperimentVariant.value === UpgradeModalCopyAExperimentVariant.Control
    ? defaultTitle
    : __('Cancel anytime with our flexible refund policy.')
}
</script>

<template>
  <OzScreenSizeAwareCenterModal
    :is-draggable-drawer="false"
    :is-phone="isSmallerThanTabletPortrait"
    :window-height="windowHeight"
    :x-scrim="true"
    @scrim-click="
      isProcessingUpgrade ||
      isLoadingCancellationRefundInformation ||
      hasUserTriggeredUpgrade ||
      upgradeStep === UpgradeStep.EstimatePlatinumCost ||
      upgradeStep === UpgradeStep.EstimateUpgradePlatinumPlanCost ||
      upgradeStep === UpgradeStep.EstimateUpgradeGoldPlanCost
        ? null
        : $emit('cancel')
    "
    @scrim-esc="
      isProcessingUpgrade ||
      isLoadingCancellationRefundInformation ||
      hasUserTriggeredUpgrade ||
      upgradeStep === UpgradeStep.EstimatePlatinumCost ||
      upgradeStep === UpgradeStep.EstimateUpgradePlatinumPlanCost ||
      upgradeStep === UpgradeStep.EstimateUpgradeGoldPlanCost
        ? null
        : $emit('cancel')
    "
  >
    <template #body>
      <div class="p-4" data-testid="membershipTierUpgradeModal">
        <!-- Show tiers -->
        <template v-if="upgradeStep === UpgradeStep.ChooseTier || upgradeStep === UpgradeStep.ChooseTierFrameQuota">
          <OzCloseButton :disabled="hasUserTriggeredUpgrade" @click="$emit('cancel')" />
          <DashboardSettingsBillingModalTitle
            :title="getTierSelectionTitleFromExperiment(__('We see you flying. Maybe it’s time to soar?'))"
            :subtitle="getTierSelectionSubtitleFromExperiment(__('Upgrade to get the most out of Padlet.'))"
          />
          <div class="tablet-portrait:mx-2 mb-4 tablet-portrait:mb-6">
            <OzContainedButton
              v-if="xGetAnotherFreePadletButton"
              :text="__('Get another free padlet')"
              :color-scheme="OzContainedButtonColorScheme.Secondary"
              :size-preset="OzContainedButtonSizePreset.H40px"
              class="mb-4 tablet-portrait:mb-5"
              @click="getAnotherFreePadlet"
            />
            <PlatinumMembershipTier
              class="mb-4 tablet-portrait:mb-3 p-4 tablet-portrait:py-3 tablet-portrait:px-6"
              :is-expanded="!isSmallerThanTabletPortrait"
              :variant="platinumMembershipTierVariant"
              :is-loading-plan-information="!plansFetched"
              :monthly-price="xPlatinumMonthlyPrice ? displayedPrices.platinum.monthly : ''"
              :annual-price="displayedPrices.platinum.annual"
              :advertised-file-size-quota="displayedQuotas.platinum.max_upload_megabytes"
              :is-loading-advertised-quotas="isLoadingAdvertisedQuotas"
              :is-whiteboard="xFrameLimitInsteadOfFileLimit"
              :advertised-whiteboard-frame-quota="displayedQuotas.platinum.max_whiteboard_frames"
              :annual-savings-percentage="
                isAppUsing('higherMonthlyPricingPlans')
                  ? personalPlansStore.getAnnualSavingsPercentageForTier('platinum')
                  : undefined
              "
              @upgrade="initiateUpgradeToPlatinum"
              @upgrade-monthly="
                handleUpgrade({
                  paymentSchedule: PaymentSchedule.Monthly,
                  tier: Tier.Platinum,
                  postCheckoutRedirectUrl: membershipConfirmationPageUrl,
                })
              "
              @upgrade-annual="
                handleUpgrade({
                  paymentSchedule: PaymentSchedule.Annual,
                  tier: Tier.Platinum,
                  postCheckoutRedirectUrl: membershipConfirmationPageUrl,
                })
              "
            />
            <GoldMembershipTier
              v-if="xGoldPlans"
              class="mb-4 tablet-portrait:mb-3 p-4 tablet-portrait:py-3 tablet-portrait:px-6"
              :is-expanded="!isSmallerThanTabletPortrait"
              :walls-used="user.quota.walls_used"
              :whiteboard_frames_used="whiteboardFramesUsed"
              :x-checked="user.membership_tier === 'gold'"
              :variant="padletQuotaGoldMembershipTierVariant"
              :is-loading-plan-information="!plansFetched"
              :monthly-price="xGoldMonthlyPrice ? displayedPrices.gold.monthly : ''"
              :annual-price="displayedPrices.gold.annual"
              :advertised-walls-quota="displayedQuotas.gold.max_walls"
              :advertised-file-size-quota="displayedQuotas.gold.max_upload_megabytes"
              :is-loading-advertised-quotas="isLoadingAdvertisedQuotas"
              :is-whiteboard="xFrameLimitInsteadOfFileLimit"
              :whiteboard-frames-used="whiteboardFramesUsed"
              :advertised-whiteboard-frame-quota="displayedQuotas.gold.max_whiteboard_frames"
              :annual-savings-percentage="
                isAppUsing('higherMonthlyPricingPlans')
                  ? personalPlansStore.getAnnualSavingsPercentageForTier('gold')
                  : undefined
              "
              @upgrade="initiateUpgradeToGold"
              @upgrade-monthly="
                handleUpgrade({
                  paymentSchedule: PaymentSchedule.Monthly,
                  tier: Tier.Gold,
                  postCheckoutRedirectUrl: membershipConfirmationPageUrl,
                })
              "
              @upgrade-annual="
                handleUpgrade({
                  paymentSchedule: PaymentSchedule.Annual,
                  tier: Tier.Gold,
                  postCheckoutRedirectUrl: membershipConfirmationPageUrl,
                })
              "
            />
            <SilverMembershipTier
              v-if="xSilverPlans"
              class="mb-4 tablet-portrait:mb-3 p-4 tablet-portrait:py-3 tablet-portrait:px-6"
              :is-expanded="!isSmallerThanTabletPortrait"
              :walls-used="user.quota.walls_used"
              :whiteboard_frames_used="whiteboardFramesUsed"
              :x-checked="user.membership_tier === 'silver'"
              :variant="padletQuotaSilverMembershipTierVariant"
              :is-loading-plan-information="!plansFetched"
              :annual-price="displayedPrices.silver.annual"
              :advertised-walls-quota="displayedQuotas.silver.max_walls"
              :advertised-file-size-quota="displayedQuotas.silver.max_upload_megabytes"
              :is-loading-advertised-quotas="isLoadingAdvertisedQuotas"
              :is-whiteboard="xFrameLimitInsteadOfFileLimit"
              :whiteboard-frames-used="whiteboardFramesUsed"
              :advertised-whiteboard-frame-quota="displayedQuotas.silver.max_whiteboard_frames"
              @upgrade="initiateUpgradeToSilver"
              @upgrade-monthly="
                handleUpgrade({
                  paymentSchedule: PaymentSchedule.Monthly,
                  tier: Tier.Silver,
                  postCheckoutRedirectUrl: membershipConfirmationPageUrl,
                })
              "
              @upgrade-annual="
                handleUpgrade({
                  paymentSchedule: PaymentSchedule.Annual,
                  tier: Tier.Silver,
                  postCheckoutRedirectUrl: membershipConfirmationPageUrl,
                })
              "
            />
            <NeonMembershipTier
              class="p-4 tablet-portrait:py-3 tablet-portrait:px-6"
              :is-expanded="!isSmallerThanTabletPortrait"
              :walls-used="user.quota.walls_used"
              :whiteboard_frames_used="whiteboardFramesUsed"
              :x-checked="user.membership_tier === 'neon'"
              :variant="padletQuotaNeonMembershipTierVariant"
              :is-loading-plan-information="!plansFetched"
              :advertised-walls-quota="displayedQuotas.neon.max_walls"
              :advertised-file-size-quota="displayedQuotas.neon.max_upload_megabytes"
              :is-whiteboard="xFrameLimitInsteadOfFileLimit"
              :whiteboard-frames-used="whiteboardFramesUsed"
              :advertised-whiteboard-frame-quota="displayedQuotas.neon.max_whiteboard_frames"
              :is-loading-advertised-quotas="isLoadingAdvertisedQuotas"
            />
          </div>
        </template>
        <!-- Show tiers when over padlet quota -->
        <template v-else-if="upgradeStep === UpgradeStep.ChooseTierPadletQuota">
          <OzCloseButton :disabled="hasUserTriggeredUpgrade" @click="$emit('cancel')" />
          <DashboardSettingsBillingModalTitle
            :title="getTierSelectionTitleFromExperiment(chooseTierPadletQuotaTitle)"
            :subtitle="getTierSelectionSubtitleFromExperiment(chooseTierPadletQuotaSubtitle)"
          />
          <div class="tablet-portrait:mx-2 mb-4 tablet-portrait:mb-6">
            <OzContainedButton
              v-if="xGetAnotherFreePadletButton"
              :text="__('Get another free padlet')"
              :color-scheme="OzContainedButtonColorScheme.Secondary"
              :size-preset="OzContainedButtonSizePreset.H40px"
              class="mb-4 tablet-portrait:mb-5"
              @click="getAnotherFreePadlet"
            />
            <PlatinumMembershipTier
              class="mb-4 tablet-portrait:mb-3 p-4 tablet-portrait:py-3 tablet-portrait:px-6"
              :is-expanded="!isSmallerThanTabletPortrait"
              :variant="platinumMembershipTierVariant"
              :is-loading-plan-information="!plansFetched"
              :monthly-price="xPlatinumMonthlyPrice ? displayedPrices.platinum.monthly : ''"
              :annual-price="displayedPrices.platinum.annual"
              :advertised-file-size-quota="displayedQuotas.platinum.max_upload_megabytes"
              :is-loading-advertised-quotas="isLoadingAdvertisedQuotas"
              :is-whiteboard="xFrameLimitInsteadOfFileLimit"
              :advertised-whiteboard-frame-quota="displayedQuotas.platinum.max_whiteboard_frames"
              :annual-savings-percentage="
                isAppUsing('higherMonthlyPricingPlans')
                  ? personalPlansStore.getAnnualSavingsPercentageForTier('platinum')
                  : undefined
              "
              @upgrade="initiateUpgradeToPlatinum"
              @upgrade-monthly="
                handleUpgrade({
                  paymentSchedule: PaymentSchedule.Monthly,
                  tier: Tier.Platinum,
                  postCheckoutRedirectUrl: membershipConfirmationPageUrl,
                })
              "
              @upgrade-annual="
                handleUpgrade({
                  paymentSchedule: PaymentSchedule.Annual,
                  tier: Tier.Platinum,
                  postCheckoutRedirectUrl: membershipConfirmationPageUrl,
                })
              "
            />
            <GoldMembershipTier
              v-if="xGoldPlans"
              class="mb-4 tablet-portrait:mb-3 p-4 tablet-portrait:py-3 tablet-portrait:px-6"
              :is-expanded="!isSmallerThanTabletPortrait"
              :walls-used="user.quota.walls_used"
              :whiteboard_frames_used="whiteboardFramesUsed"
              :x-checked="user.membership_tier === 'gold'"
              :variant="padletQuotaGoldMembershipTierVariant"
              :is-loading-plan-information="!plansFetched"
              :monthly-price="xGoldMonthlyPrice ? displayedPrices.gold.monthly : ''"
              :annual-price="displayedPrices.gold.annual"
              :advertised-walls-quota="displayedQuotas.gold.max_walls"
              :advertised-file-size-quota="displayedQuotas.gold.max_upload_megabytes"
              :is-loading-advertised-quotas="isLoadingAdvertisedQuotas"
              :is-whiteboard="xFrameLimitInsteadOfFileLimit"
              :whiteboard-frames-used="whiteboardFramesUsed"
              :advertised-whiteboard-frame-quota="displayedQuotas.gold.max_whiteboard_frames"
              :annual-savings-percentage="
                isAppUsing('higherMonthlyPricingPlans')
                  ? personalPlansStore.getAnnualSavingsPercentageForTier('gold')
                  : undefined
              "
              @upgrade="initiateUpgradeToGold"
              @upgrade-monthly="
                handleUpgrade({
                  paymentSchedule: PaymentSchedule.Monthly,
                  tier: Tier.Gold,
                  postCheckoutRedirectUrl: membershipConfirmationPageUrl,
                })
              "
              @upgrade-annual="
                handleUpgrade({
                  paymentSchedule: PaymentSchedule.Annual,
                  tier: Tier.Gold,
                  postCheckoutRedirectUrl: membershipConfirmationPageUrl,
                })
              "
            />
            <SilverMembershipTier
              v-if="xSilverPlans"
              class="mb-4 tablet-portrait:mb-3 p-4 tablet-portrait:py-3 tablet-portrait:px-6"
              :is-expanded="!isSmallerThanTabletPortrait"
              :walls-used="user.quota.walls_used"
              :whiteboard_frames_used="whiteboardFramesUsed"
              :x-checked="user.membership_tier === 'silver'"
              :variant="padletQuotaSilverMembershipTierVariant"
              :is-loading-plan-information="!plansFetched"
              :annual-price="displayedPrices.silver.annual"
              :advertised-walls-quota="displayedQuotas.silver.max_walls"
              :advertised-file-size-quota="displayedQuotas.silver.max_upload_megabytes"
              :is-loading-advertised-quotas="isLoadingAdvertisedQuotas"
              :is-whiteboard="xFrameLimitInsteadOfFileLimit"
              :whiteboard-frames-used="whiteboardFramesUsed"
              :advertised-whiteboard-frame-quota="displayedQuotas.silver.max_whiteboard_frames"
              @upgrade="initiateUpgradeToSilver"
              @upgrade-monthly="
                handleUpgrade({
                  paymentSchedule: PaymentSchedule.Monthly,
                  tier: Tier.Silver,
                  postCheckoutRedirectUrl: membershipConfirmationPageUrl,
                })
              "
              @upgrade-annual="
                handleUpgrade({
                  paymentSchedule: PaymentSchedule.Annual,
                  tier: Tier.Silver,
                  postCheckoutRedirectUrl: membershipConfirmationPageUrl,
                })
              "
            />
            <NeonMembershipTier
              class="p-4 tablet-portrait:py-3 tablet-portrait:px-6"
              :is-expanded="!isSmallerThanTabletPortrait"
              :walls-used="user.quota.walls_used"
              :whiteboard_frames_used="whiteboardFramesUsed"
              :x-checked="user.membership_tier === 'neon'"
              :variant="padletQuotaNeonMembershipTierVariant"
              :is-loading-plan-information="!plansFetched"
              :advertised-walls-quota="displayedQuotas.neon.max_walls"
              :advertised-file-size-quota="displayedQuotas.neon.max_upload_megabytes"
              :is-whiteboard="xFrameLimitInsteadOfFileLimit"
              :whiteboard-frames-used="whiteboardFramesUsed"
              :advertised-whiteboard-frame-quota="displayedQuotas.neon.max_whiteboard_frames"
              :is-loading-advertised-quotas="isLoadingAdvertisedQuotas"
            />
          </div>
        </template>
        <!-- Show tiers when over file size quota -->
        <template v-else-if="upgradeStep === UpgradeStep.ChooseTierFileSizeQuota">
          <OzCloseButton :disabled="hasUserTriggeredUpgrade" @click="$emit('cancel')" />
          <DashboardSettingsBillingModalTitle
            :title="getTierSelectionTitleFromExperiment(__('We see you flying. Maybe it’s time to soar?'))"
            :subtitle="getTierSelectionSubtitleFromExperiment(__('Upgrade your membership to upload bigger files.'))"
          />
          <div class="tablet-portrait:mx-2 mb-4 tablet-portrait:mb-2">
            <OzContainedButton
              v-if="xGetAnotherFreePadletButton"
              :text="__('Get another free padlet')"
              :color-scheme="OzContainedButtonColorScheme.Secondary"
              :size-preset="OzContainedButtonSizePreset.H40px"
              class="mb-4 tablet-portrait:mb-5"
              @click="getAnotherFreePadlet"
            />
            <PlatinumMembershipTier
              class="mb-4 tablet-portrait:mb-3 p-4 tablet-portrait:py-3 tablet-portrait:px-6"
              :is-expanded="!isSmallerThanTabletPortrait"
              :variant="platinumMembershipTierVariant"
              :is-loading-plan-information="!plansFetched"
              :monthly-price="xPlatinumMonthlyPrice ? displayedPrices.platinum.monthly : ''"
              :annual-price="displayedPrices.platinum.annual"
              :advertised-file-size-quota="displayedQuotas.platinum.max_upload_megabytes"
              :is-loading-advertised-quotas="isLoadingAdvertisedQuotas"
              :is-whiteboard="xFrameLimitInsteadOfFileLimit"
              :advertised-whiteboard-frame-quota="displayedQuotas.platinum.max_whiteboard_frames"
              :annual-savings-percentage="
                isAppUsing('higherMonthlyPricingPlans')
                  ? personalPlansStore.getAnnualSavingsPercentageForTier('platinum')
                  : undefined
              "
              @upgrade="initiateUpgradeToPlatinum"
              @upgrade-monthly="
                handleUpgrade({
                  paymentSchedule: PaymentSchedule.Monthly,
                  tier: Tier.Platinum,
                  postCheckoutRedirectUrl: membershipConfirmationPageUrl,
                })
              "
              @upgrade-annual="
                handleUpgrade({
                  paymentSchedule: PaymentSchedule.Annual,
                  tier: Tier.Platinum,
                  postCheckoutRedirectUrl: membershipConfirmationPageUrl,
                })
              "
            />
            <GoldMembershipTier
              v-if="xGoldPlans"
              class="mb-4 tablet-portrait:mb-3 p-4 tablet-portrait:py-3 tablet-portrait:px-6"
              :is-expanded="!isSmallerThanTabletPortrait"
              :walls-used="user.quota.walls_used"
              :x-checked="user.membership_tier === 'gold'"
              :variant="fileSizeQuotaGoldMembershipTierVariant"
              :file-size="sizeOfFileOverQuota"
              :is-loading-plan-information="!plansFetched"
              :monthly-price="xGoldMonthlyPrice ? displayedPrices.gold.monthly : ''"
              :annual-price="displayedPrices.gold.annual"
              :advertised-walls-quota="displayedQuotas.gold.max_walls"
              :advertised-file-size-quota="displayedQuotas.gold.max_upload_megabytes"
              :is-loading-advertised-quotas="isLoadingAdvertisedQuotas"
              :is-whiteboard="xFrameLimitInsteadOfFileLimit"
              :whiteboard-frames-used="whiteboardFramesUsed"
              :advertised-whiteboard-frame-quota="displayedQuotas.gold.max_whiteboard_frames"
              :annual-savings-percentage="
                isAppUsing('higherMonthlyPricingPlans')
                  ? personalPlansStore.getAnnualSavingsPercentageForTier('gold')
                  : undefined
              "
              @upgrade="initiateUpgradeToGold"
              @upgrade-monthly="
                handleUpgrade({
                  paymentSchedule: PaymentSchedule.Monthly,
                  tier: Tier.Gold,
                  postCheckoutRedirectUrl: membershipConfirmationPageUrl,
                })
              "
              @upgrade-annual="
                handleUpgrade({
                  paymentSchedule: PaymentSchedule.Annual,
                  tier: Tier.Gold,
                  postCheckoutRedirectUrl: membershipConfirmationPageUrl,
                })
              "
            />
            <SilverMembershipTier
              v-if="xSilverPlans"
              class="mb-4 tablet-portrait:mb-3 p-4 tablet-portrait:py-3 tablet-portrait:px-6"
              :is-expanded="!isSmallerThanTabletPortrait"
              :walls-used="user.quota.walls_used"
              :x-checked="user.membership_tier === 'silver'"
              :variant="fileSizeQuotaSilverMembershipTierVariant"
              :is-loading-plan-information="!plansFetched"
              :annual-price="displayedPrices.silver.annual"
              :advertised-walls-quota="displayedQuotas.silver.max_walls"
              :advertised-file-size-quota="displayedQuotas.silver.max_upload_megabytes"
              :is-loading-advertised-quotas="isLoadingAdvertisedQuotas"
              :is-whiteboard="xFrameLimitInsteadOfFileLimit"
              :whiteboard-frames-used="whiteboardFramesUsed"
              :advertised-whiteboard-frame-quota="displayedQuotas.silver.max_whiteboard_frames"
              @upgrade="initiateUpgradeToSilver"
              @upgrade-monthly="
                handleUpgrade({
                  paymentSchedule: PaymentSchedule.Monthly,
                  tier: Tier.Silver,
                  postCheckoutRedirectUrl: membershipConfirmationPageUrl,
                })
              "
              @upgrade-annual="
                handleUpgrade({
                  paymentSchedule: PaymentSchedule.Annual,
                  tier: Tier.Silver,
                  postCheckoutRedirectUrl: membershipConfirmationPageUrl,
                })
              "
            />
            <NeonMembershipTier
              class="p-4 tablet-portrait:py-3 tablet-portrait:px-6"
              :is-expanded="!isSmallerThanTabletPortrait"
              :walls-used="user.quota.walls_used"
              :x-checked="user.membership_tier === 'neon'"
              :variant="NeonMembershipTierVariant.FileSize"
              :file-size="sizeOfFileOverQuota"
              :is-loading-plan-information="!plansFetched"
              :advertised-walls-quota="displayedQuotas.neon.max_walls"
              :advertised-file-size-quota="displayedQuotas.neon.max_upload_megabytes"
              :is-whiteboard="xFrameLimitInsteadOfFileLimit"
              :whiteboard-frames-used="whiteboardFramesUsed"
              :advertised-whiteboard-frame-quota="displayedQuotas.neon.max_whiteboard_frames"
              :is-loading-advertised-quotas="isLoadingAdvertisedQuotas"
            />
          </div>
        </template>
        <!-- Show tiers when user wants to upgrade to record longer videos -->
        <template v-else-if="upgradeStep === UpgradeStep.ChooseTierVideoRecorderQuota">
          <OzCloseButton
            :disabled="hasUserTriggeredUpgrade"
            data-testid="membershipTierUpgradeModalCloseButton"
            @click="$emit('cancel')"
          />
          <DashboardSettingsBillingModalTitle
            :title="__('We love that you love video.')"
            :subtitle="__('Upgrade to record longer videos.')"
          />
          <div class="tablet-portrait:mx-2 mb-4 tablet-portrait:mb-2">
            <PlatinumMembershipTier
              class="mb-4 tablet-portrait:mb-3 p-4 tablet-portrait:py-3 tablet-portrait:px-6"
              :is-expanded="!isSmallerThanTabletPortrait"
              :variant="PlatinumMembershipTierVariant.Upgrade"
              :is-loading-plan-information="!plansFetched"
              :monthly-price="xPlatinumMonthlyPrice ? displayedPrices.platinum.monthly : ''"
              :annual-price="displayedPrices.platinum.annual"
              :advertised-file-size-quota="displayedQuotas.platinum.max_upload_megabytes"
              :is-loading-advertised-quotas="isLoadingAdvertisedQuotas"
              :is-whiteboard="xFrameLimitInsteadOfFileLimit"
              :advertised-whiteboard-frame-quota="displayedQuotas.platinum.max_whiteboard_frames"
              :annual-savings-percentage="
                isAppUsing('higherMonthlyPricingPlans')
                  ? personalPlansStore.getAnnualSavingsPercentageForTier('platinum')
                  : undefined
              "
              @upgrade="initiateUpgradeToPlatinum"
              @upgrade-monthly="
                handleUpgrade({
                  paymentSchedule: PaymentSchedule.Monthly,
                  tier: Tier.Platinum,
                  postCheckoutRedirectUrl: membershipConfirmationPageUrl,
                })
              "
              @upgrade-annual="
                handleUpgrade({
                  paymentSchedule: PaymentSchedule.Annual,
                  tier: Tier.Platinum,
                  postCheckoutRedirectUrl: membershipConfirmationPageUrl,
                })
              "
            >
              <template #secondaryBenefitText>
                <p>{{ displayedVideoLengthQuotas.platinum }}</p>
              </template>
            </PlatinumMembershipTier>
            <GoldMembershipTier
              v-if="xGoldPlans"
              class="mb-4 tablet-portrait:mb-3 p-4 tablet-portrait:py-3 tablet-portrait:px-6"
              :is-expanded="!isSmallerThanTabletPortrait"
              :variant="GoldMembershipTierVariant.Upgrade"
              :color-scheme="user.membership_tier === 'gold' ? OzBoxColors.Secondary : undefined"
              :x-checked="user.membership_tier === 'gold'"
              :is-loading-plan-information="!plansFetched"
              :monthly-price="xGoldMonthlyPrice ? displayedPrices.gold.monthly : ''"
              :annual-price="displayedPrices.gold.annual"
              :advertised-walls-quota="displayedQuotas.gold.max_walls"
              :advertised-file-size-quota="displayedQuotas.gold.max_upload_megabytes"
              :is-loading-advertised-quotas="isLoadingAdvertisedQuotas"
              :is-whiteboard="xFrameLimitInsteadOfFileLimit"
              :whiteboard-frames-used="whiteboardFramesUsed"
              :advertised-whiteboard-frame-quota="displayedQuotas.gold.max_whiteboard_frames"
              :annual-savings-percentage="
                isAppUsing('higherMonthlyPricingPlans')
                  ? personalPlansStore.getAnnualSavingsPercentageForTier('gold')
                  : undefined
              "
              @upgrade="initiateUpgradeToGold"
              @upgrade-monthly="
                handleUpgrade({
                  paymentSchedule: PaymentSchedule.Monthly,
                  tier: Tier.Gold,
                  postCheckoutRedirectUrl: membershipConfirmationPageUrl,
                })
              "
              @upgrade-annual="
                handleUpgrade({
                  paymentSchedule: PaymentSchedule.Annual,
                  tier: Tier.Gold,
                  postCheckoutRedirectUrl: membershipConfirmationPageUrl,
                })
              "
            >
              <template #secondaryBenefitText>
                <p>{{ displayedVideoLengthQuotas.gold }}</p>
              </template>

              <!-- If user is already on gold plan, just show a "current plan" text -->
              <template v-if="user.membership_tier === 'gold'" #endContent>
                <p
                  :class="[
                    'text-end text-body-small font-semibold',
                    !isSmallerThanTabletPortrait && 'pe-2',
                    isSmallerThanTabletPortrait && 'pe-1',
                  ]"
                >
                  {{ __('Current plan') }}
                </p>
              </template>
            </GoldMembershipTier>
            <!-- Sept 2024: new users will not see silver anymore; however we need to show this to users who had seen or upgraded to the silver plan before -->
            <SilverMembershipTier
              v-if="xSilverPlans"
              class="mb-4 tablet-portrait:mb-3 p-4 tablet-portrait:py-3 tablet-portrait:px-6"
              :is-expanded="!isSmallerThanTabletPortrait"
              :variant="SilverMembershipTierVariant.Upgrade"
              :x-checked="user.membership_tier === 'silver'"
              :is-whiteboard="xFrameLimitInsteadOfFileLimit"
              :is-loading-plan-information="!plansFetched"
              :is-loading-advertised-quotas="isLoadingAdvertisedQuotas"
              :advertised-whiteboard-frame-quota="displayedQuotas.silver.max_whiteboard_frames"
              :advertised-file-size-quota="displayedQuotas.silver.max_upload_megabytes"
              :advertised-walls-quota="displayedQuotas.silver.max_walls"
              :annual-price="displayedPrices.silver.annual"
              :color-scheme="user.membership_tier === 'silver' ? OzBoxColors.Secondary : undefined"
              @upgrade="initiateUpgradeToSilver"
              @upgrade-monthly="
                handleUpgrade({
                  paymentSchedule: PaymentSchedule.Monthly,
                  tier: Tier.Silver,
                  postCheckoutRedirectUrl: membershipConfirmationPageUrl,
                })
              "
              @upgrade-annual="
                handleUpgrade({
                  paymentSchedule: PaymentSchedule.Annual,
                  tier: Tier.Silver,
                  postCheckoutRedirectUrl: membershipConfirmationPageUrl,
                })
              "
            >
              <template #secondaryBenefitText>
                <p>{{ displayedVideoLengthQuotas.silver }}</p>
              </template>

              <!-- If user is already on silver plan, just show a "current plan" text -->
              <template v-if="user.membership_tier === 'silver'" #endContent>
                <p
                  :class="[
                    'text-end text-body-small font-semibold',
                    !isSmallerThanTabletPortrait && 'pe-2',
                    isSmallerThanTabletPortrait && 'pe-1',
                  ]"
                >
                  {{ __('Current plan') }}
                </p>
              </template>
            </SilverMembershipTier>
            <NeonMembershipTier
              v-if="user.membership_tier === 'neon'"
              class="p-4 tablet-portrait:py-3 tablet-portrait:px-6"
              :variant="NeonMembershipTierVariant.Default"
              :is-whiteboard="xFrameLimitInsteadOfFileLimit"
              :is-expanded="!isSmallerThanTabletPortrait"
              :is-loading-plan-information="!plansFetched"
              :is-loading-advertised-quotas="isLoadingAdvertisedQuotas"
              :advertised-walls-quota="displayedQuotas.neon.max_walls"
              :advertised-file-size-quota="displayedQuotas.neon.max_upload_megabytes"
              :advertised-whiteboard-frame-quota="displayedQuotas.neon.max_whiteboard_frames"
              :walls-used="user.quota.walls_used"
              :x-checked="user.membership_tier === 'neon'"
              :color-scheme="user.membership_tier === 'neon' ? OzBoxColors.Secondary : undefined"
            >
              <template #secondaryBenefitText>
                <p>{{ displayedVideoLengthQuotas.neon }}</p>
              </template>

              <!-- If user is already on neon plan, just show a "current plan" text -->
              <template v-if="user.membership_tier === 'neon'" #endContent>
                <p
                  :class="[
                    'text-end text-body-small font-semibold',
                    !isSmallerThanTabletPortrait && 'pe-2',
                    isSmallerThanTabletPortrait && 'pe-1',
                  ]"
                >
                  {{ __('Current plan') }}
                </p>
              </template>
            </NeonMembershipTier>
          </div>
        </template>
        <!-- Show Gold payment schedules -->
        <template v-else-if="upgradeStep === UpgradeStep.ScheduleGold">
          <OzCloseButton @click="$emit('cancel')" />
          <DashboardSettingsBillingModalTitle :title="__('Upgrade to Gold')" :subtitle="__('Choose payment plan')" />
          <OzBox class="mb-4 py-1 tablet-portrait:mx-2 tablet-portrait:mb-6" :color="OzBoxColors.Secondary">
            <PaymentScheduleTier
              class="px-4 py-3 tablet-portrait:px-6"
              :tier="Tier.Gold"
              :payment-schedule="PaymentSchedule.Monthly"
              :use-tier-color="false"
              :upgrade-source="upgradeSource"
              :plans="plans"
              :is-loading-plan-information="!plansFetched"
              :is-expanded="!isSmallerThanTabletPortrait"
              @upgrade="
                handleUpgrade({
                  paymentSchedule: PaymentSchedule.Monthly,
                  tier: Tier.Gold,
                  postCheckoutRedirectUrl: membershipConfirmationPageUrl,
                })
              "
            />
            <OzDivider
              :color-scheme="OzDividerColorScheme.Solid"
              class="mx-4 tablet-portrait:ms-5 tablet-portrait:me-6"
            />
            <PaymentScheduleTier
              class="px-4 py-3 tablet-portrait:px-6"
              :tier="Tier.Gold"
              :payment-schedule="PaymentSchedule.Annual"
              :use-tier-color="false"
              :upgrade-source="upgradeSource"
              :plans="plans"
              :is-loading-plan-information="!plansFetched"
              :is-expanded="!isSmallerThanTabletPortrait"
              @upgrade="
                handleUpgrade({
                  paymentSchedule: PaymentSchedule.Annual,
                  tier: Tier.Gold,
                  postCheckoutRedirectUrl: membershipConfirmationPageUrl,
                })
              "
            />
          </OzBox>
          <p
            class="mb-4 px-1 tablet-portrait:mb-2 tablet-portrait:px-4 text-body-extra-small text-dark-text-200 dark:text-light-text-200"
          >
            {{ __('*Subscription auto-renews at the end of the payment period. You can cancel at any time.') }}
          </p>
        </template>
        <!-- Show Platinum payment schedules -->
        <template v-else-if="upgradeStep === UpgradeStep.SchedulePlatinum">
          <OzCloseButton @click="$emit('cancel')" />
          <DashboardSettingsBillingModalTitle
            :title="__('Upgrade to Platinum')"
            :subtitle="__('Choose payment plan')"
          />
          <OzBox class="mb-4 py-1 tablet-portrait:mx-2 tablet-portrait:mb-6" :color="OzBoxColors.Secondary">
            <PaymentScheduleTier
              class="px-4 py-3 tablet-portrait:px-6"
              :tier="Tier.Platinum"
              :payment-schedule="PaymentSchedule.Monthly"
              :use-tier-color="false"
              :upgrade-source="upgradeSource"
              :plans="plans"
              :is-loading-plan-information="!plansFetched"
              :is-expanded="!isSmallerThanTabletPortrait"
              @upgrade="
                handleUpgrade({
                  paymentSchedule: PaymentSchedule.Monthly,
                  tier: Tier.Platinum,
                  postCheckoutRedirectUrl: membershipConfirmationPageUrl,
                })
              "
            />
            <OzDivider
              :color-scheme="OzDividerColorScheme.Solid"
              class="mx-4 tablet-portrait:ms-5 tablet-portrait:me-6"
            />
            <PaymentScheduleTier
              class="px-4 py-3 tablet-portrait:px-6"
              :tier="Tier.Platinum"
              :payment-schedule="PaymentSchedule.Annual"
              :use-tier-color="false"
              :upgrade-source="upgradeSource"
              :plans="plans"
              :is-loading-plan-information="!plansFetched"
              :is-expanded="!isSmallerThanTabletPortrait"
              @upgrade="
                handleUpgrade({
                  paymentSchedule: PaymentSchedule.Annual,
                  tier: Tier.Platinum,
                  postCheckoutRedirectUrl: membershipConfirmationPageUrl,
                })
              "
            />
          </OzBox>
          <p
            class="mb-4 px-1 tablet-portrait:mb-2 tablet-portrait:px-4 text-body-extra-small text-dark-text-200 dark:text-light-text-200"
          >
            {{ __('*Subscription auto-renews at the end of the payment period. You can cancel at any time.') }}
          </p>
        </template>
        <!-- Show loading spinner while fetching the Platinum upgrade cost -->
        <template v-else-if="upgradeStep === UpgradeStep.EstimatePlatinumCost">
          <OzCloseButton :disabled="true" @click="$emit('cancel')" />
          <DashboardSettingsBillingModalTitle :title="__('Upgrade to Platinum')" :subtitle="__('Confirm changes')" />
          <div class="flex justify-center py-7">
            <OzIndeterminateSpinner />
          </div>
        </template>
        <template
          v-else-if="
            upgradeStep === UpgradeStep.ConfirmMonthlyPlatinum || upgradeStep === UpgradeStep.ConfirmAnnualPlatinum
          "
        >
          <OzCloseButton :disabled="isProcessingUpgrade" @click="$emit('cancel')" />
          <DashboardSettingsBillingModalTitle :title="__('Upgrade to Platinum')" :subtitle="__('Confirm changes')" />
          <div
            v-if="upgradeError"
            :class="[
              'mx-0 tablet-portrait:mx-4 mb-4 tablet-portrait:mb-6',
              'py-7',
              'flex flex-col items-center',
              'text-dark-text-100 dark:text-light-text-100',
            ]"
          >
            {{ upgradeError }}
          </div>
          <div v-else class="mx-0 tablet-portrait:mx-2">
            <!-- Show Platinum monthly payment schedule information -->
            <BillingCollectionItems
              v-if="upgradeStep === UpgradeStep.ConfirmMonthlyPlatinum"
              class="mb-4 tablet-portrait:mb-3"
              :items="[
                __('You are upgrading to <strong>Platinum</strong> starting now (<strong>%{currentDate}</strong>).', {
                  currentDate,
                }),
                __('Your recurring charges will be <strong>%{amount}</strong> a month.', {
                  amount: displayedPrices.platinum.monthly,
                }),
                __('You can cancel or change your membership anytime.'),
              ]"
            />
            <!-- Show Platinum annual payment schedule information -->
            <BillingCollectionItems
              v-if="upgradeStep === UpgradeStep.ConfirmAnnualPlatinum"
              class="mb-4 tablet-portrait:mb-3"
              :items="[
                __('You are upgrading to <strong>Platinum</strong> starting now (<strong>%{currentDate}</strong>).', {
                  currentDate,
                }),
                __('Your recurring charges will be <strong>%{amount}</strong> a year.', {
                  amount: displayedPrices.platinum.annual,
                }),
                __('You can cancel or change your membership anytime.'),
              ]"
            />
            <BillingCollectionItems
              :items="[
                __(
                  'The unused portion of your current payment period amounts to <strong>%{amountUnused}</strong>. We will apply it as a credit towards your first invoice.',
                  {
                    amountUnused: displayedEstimatedUpgradeCost.unused_portion,
                  },
                ),
                __('We will charge your card <strong>%{amount}</strong> now.', {
                  amount: displayedEstimatedUpgradeCost.amount_due,
                }),
              ]"
            />
            <div class="my-4 tablet-portrait:mt-6 tablet-portrait:mb-2 flex justify-end">
              <MembershipTierActionButton
                class="w-full tablet-portrait:w-auto"
                :button-text="__('Confirm')"
                :processing-text="__('Upgrading...')"
                :is-processing="isProcessingUpgrade"
                @click="confirmUpgrade(upgradeToPlatinum)"
              />
            </div>
          </div>
        </template>
        <template
          v-else-if="
            upgradeStep === UpgradeStep.ConfirmMonthlyPlatinumDifferentCurrency ||
            upgradeStep === UpgradeStep.ConfirmAnnualPlatinumDifferentCurrency
          "
        >
          <OzCloseButton
            :disabled="isProcessingUpgrade || isLoadingCancellationRefundInformation"
            @click="$emit('cancel')"
          />
          <DashboardSettingsBillingModalTitle :title="__('Upgrade to Platinum')" :subtitle="__('Confirm changes')" />
          <div
            v-if="upgradeError"
            :class="[
              'mx-0 tablet-portrait:mx-4 mb-4 tablet-portrait:mb-6',
              'py-7',
              'flex flex-col items-center',
              'text-dark-text-100 dark:text-light-text-100',
            ]"
          >
            {{ upgradeError }}
          </div>
          <div v-else-if="isLoadingCancellationRefundInformation" class="flex justify-center py-7">
            <OzIndeterminateSpinner />
          </div>
          <div v-else class="mx-0 tablet-portrait:mx-2">
            <!-- Show Platinum monthly payment schedule information -->
            <template v-if="upgradeStep === UpgradeStep.ConfirmMonthlyPlatinumDifferentCurrency">
              <BillingCollectionItems
                class="mb-4 tablet-portrait:mb-3"
                :items="[
                  __('You are upgrading to <strong>Platinum</strong> starting now (<strong>%{currentDate}</strong>).', {
                    currentDate,
                  }),
                  __('Your recurring charges will be <strong>%{amount}</strong> a month.', {
                    amount: displayedPrices.platinum.monthly,
                  }),
                  __('You can cancel or change your membership anytime.'),
                ]"
              />
              <BillingCollectionItems
                :items="[
                  __(
                    'The unused portion of your current payment period amounts to <strong>%{refundAmount}</strong>. We will refund it to your card now.',
                    {
                      refundAmount: estimatedCancellationRefundAmount,
                    },
                  ),
                  __('We will charge your card <strong>%{amount}</strong> now.', {
                    amount: displayedPrices.platinum.monthly,
                  }),
                ]"
              />
            </template>
            <!-- Show Platinum annual payment schedule information -->
            <template v-if="upgradeStep === UpgradeStep.ConfirmAnnualPlatinumDifferentCurrency">
              <BillingCollectionItems
                class="mb-4 tablet-portrait:mb-3"
                :items="[
                  __('You are upgrading to <strong>Platinum</strong> starting now (<strong>%{currentDate}</strong>).', {
                    currentDate,
                  }),
                  __('Your recurring charges will be <strong>%{amount}</strong> a year.', {
                    amount: displayedPrices.platinum.annual,
                  }),
                  __('You can cancel or change your membership anytime.'),
                ]"
              />
              <BillingCollectionItems
                :items="[
                  __(
                    'The unused portion of your current payment period amounts to <strong>%{refundAmount}</strong>. We will refund it to your card now.',
                    {
                      refundAmount: estimatedCancellationRefundAmount,
                    },
                  ),
                  __('We will charge your card <strong>%{amount}</strong> now.', {
                    amount: displayedPrices.platinum.annual,
                  }),
                ]"
              />
            </template>
            <div class="my-4 tablet-portrait:mt-6 tablet-portrait:mb-2 flex justify-end">
              <MembershipTierActionButton
                class="w-full tablet-portrait:w-auto"
                :button-text="__('Confirm')"
                :processing-text="__('Upgrading...')"
                :is-processing="isProcessingUpgrade"
                @click="confirmUpgrade(upgradeToPlatinumDifferentCurrency)"
              />
            </div>
          </div>
        </template>
        <!-- Show loading spinner while fetching the Gold switch cost -->
        <template v-else-if="upgradeStep === UpgradeStep.EstimateUpgradeGoldPlanCost">
          <OzCloseButton :disabled="true" @click="$emit('cancel')" />
          <DashboardSettingsBillingModalTitle :title="__('Switch to Gold')" :subtitle="__('Confirm changes')" />
          <div class="flex justify-center py-7">
            <OzIndeterminateSpinner />
          </div>
        </template>
        <template
          v-else-if="
            upgradeStep === UpgradeStep.ConfirmUpgradeGoldPlanMonthly ||
            upgradeStep === UpgradeStep.ConfirmUpgradeGoldPlanAnnual
          "
        >
          <OzCloseButton :disabled="isProcessingUpgrade" @click="$emit('cancel')" />
          <DashboardSettingsBillingModalTitle :title="__('Switch to Gold')" :subtitle="__('Confirm changes')" />
          <div
            v-if="upgradeError"
            :class="[
              'mx-0 tablet-portrait:mx-4 mb-4 tablet-portrait:mb-6',
              'py-7',
              'flex flex-col items-center',
              'text-dark-text-100 dark:text-light-text-100',
            ]"
          >
            {{ upgradeError }}
          </div>
          <div v-else class="mx-0 tablet-portrait:mx-2">
            <!-- Show Gold monthly payment schedule information -->
            <BillingCollectionItems
              v-if="upgradeStep === UpgradeStep.ConfirmUpgradeGoldPlanMonthly"
              class="mb-4 tablet-portrait:mb-3"
              :items="[
                __('You are switching to <strong>Gold</strong> starting now (<strong>%{currentDate}</strong>).', {
                  currentDate,
                }),
                __('Your recurring charges will be <strong>%{amount}</strong> a month.', {
                  amount: displayedPrices.gold.monthly,
                }),
                __('You can cancel or change your membership anytime.'),
              ]"
            />
            <!-- Show Gold annual payment schedule information -->
            <BillingCollectionItems
              v-if="upgradeStep === UpgradeStep.ConfirmUpgradeGoldPlanAnnual"
              class="mb-4 tablet-portrait:mb-3"
              :items="[
                __('You are switching to <strong>Gold</strong> starting now (<strong>%{currentDate}</strong>).', {
                  currentDate,
                }),
                __('Your recurring charges will be <strong>%{amount}</strong> a year.', {
                  amount: displayedPrices.gold.annual,
                }),
                __('You can cancel or change your membership anytime.'),
              ]"
            />
            <BillingCollectionItems
              v-if="displayedEstimatedUpgradeCost.is_refund"
              :items="[
                __(
                  'The unused portion of your current payment period amounts to <strong>%{amountUnused}</strong>. We will subtract <strong>%{amount}</strong> for your first monthly payment.',
                  {
                    amountUnused: displayedEstimatedUpgradeCost.unused_portion,
                    amount: displayedEstimatedUpgradeCost.invoice_amount,
                  },
                ),
                __('We will issue a <strong>%{refundAmount}</strong> refund to your card now.', {
                  refundAmount: displayedEstimatedUpgradeCost.refund_amount,
                }),
              ]"
            />
            <BillingCollectionItems
              v-else
              :items="[
                __(
                  'The unused portion of your current payment period amounts to <strong>%{amountUnused}</strong>. We will apply it as a credit towards your first invoice.',
                  {
                    amountUnused: displayedEstimatedUpgradeCost.unused_portion,
                  },
                ),
                __('We will charge your card <strong>%{amount}</strong> now.', {
                  amount: displayedEstimatedUpgradeCost.amount_due,
                }),
              ]"
            />
            <div class="my-4 tablet-portrait:mt-6 tablet-portrait:mb-2 flex justify-end">
              <MembershipTierActionButton
                class="w-full tablet-portrait:w-auto"
                :button-text="__('Confirm')"
                :processing-text="__('Switching...')"
                :is-processing="isProcessingUpgrade"
                @click="confirmUpgrade(upgradeToGold)"
              />
            </div>
          </div>
        </template>
        <template
          v-else-if="
            upgradeStep === UpgradeStep.ConfirmUpgradeGoldPlanDifferentCurrencyMonthly ||
            upgradeStep === UpgradeStep.ConfirmUpgradeGoldPlanDifferentCurrencyAnnual
          "
        >
          <OzCloseButton
            :disabled="isProcessingUpgrade || isLoadingCancellationRefundInformation"
            @click="$emit('cancel')"
          />
          <DashboardSettingsBillingModalTitle :title="__('Switch to Gold')" :subtitle="__('Confirm changes')" />
          <div
            v-if="upgradeError"
            :class="[
              'mx-0 tablet-portrait:mx-4 mb-4 tablet-portrait:mb-6',
              'py-7',
              'flex flex-col items-center',
              'text-dark-text-100 dark:text-light-text-100',
            ]"
          >
            {{ upgradeError }}
          </div>
          <div v-else-if="isLoadingCancellationRefundInformation" class="flex justify-center py-7">
            <OzIndeterminateSpinner />
          </div>
          <div v-else class="mx-0 tablet-portrait:mx-2">
            <!-- Show Gold monthly payment schedule information -->
            <template v-if="upgradeStep === UpgradeStep.ConfirmUpgradeGoldPlanDifferentCurrencyMonthly">
              <BillingCollectionItems
                class="mb-4 tablet-portrait:mb-3"
                :items="[
                  __('You are switching to <strong>Gold</strong> starting now (<strong>%{currentDate}</strong>).', {
                    currentDate,
                  }),
                  __('Your recurring charges will be <strong>%{amount}</strong> a month.', {
                    amount: displayedPrices.gold.monthly,
                  }),
                  __('You can cancel or change your membership anytime.'),
                ]"
              />
              <BillingCollectionItems
                :items="[
                  __(
                    'The unused portion of your current payment period amounts to <strong>%{refundAmount}</strong>. We will refund it to your card now.',
                    {
                      refundAmount: estimatedCancellationRefundAmount,
                    },
                  ),
                  __('We will charge your card <strong>%{amount}</strong> now.', {
                    amount: displayedPrices.gold.monthly,
                  }),
                ]"
              />
            </template>
            <!-- Show Gold annual payment schedule information -->
            <template v-if="upgradeStep === UpgradeStep.ConfirmUpgradeGoldPlanDifferentCurrencyAnnual">
              <BillingCollectionItems
                class="mb-4 tablet-portrait:mb-3"
                :items="[
                  __('You are switching to <strong>Gold</strong> starting now (<strong>%{currentDate}</strong>).', {
                    currentDate,
                  }),
                  __('Your recurring charges will be <strong>%{amount}</strong> a year.', {
                    amount: displayedPrices.gold.annual,
                  }),
                  __('You can cancel or change your membership anytime.'),
                ]"
              />
              <BillingCollectionItems
                :items="[
                  __(
                    'The unused portion of your current payment period amounts to <strong>%{refundAmount}</strong>. We will refund it to your card now.',
                    {
                      refundAmount: estimatedCancellationRefundAmount,
                    },
                  ),
                  __('We will charge your card <strong>%{amount}</strong> now.', {
                    amount: displayedPrices.gold.annual,
                  }),
                ]"
              />
            </template>
            <div class="my-4 tablet-portrait:mt-6 tablet-portrait:mb-2 flex justify-end">
              <MembershipTierActionButton
                class="w-full tablet-portrait:w-auto"
                :button-text="__('Confirm')"
                :processing-text="__('Switching...')"
                :is-processing="isProcessingUpgrade"
                @click="confirmUpgrade(upgradeToGoldDifferentCurrency)"
              />
            </div>
          </div>
        </template>
        <!-- Show loading spinner while fetching the Platinum switch cost -->
        <template v-else-if="upgradeStep === UpgradeStep.EstimateUpgradePlatinumPlanCost">
          <OzCloseButton :disabled="true" @click="$emit('cancel')" />
          <DashboardSettingsBillingModalTitle :title="__('Switch to Platinum')" :subtitle="__('Confirm changes')" />
          <div class="flex justify-center py-7">
            <OzIndeterminateSpinner />
          </div>
        </template>
        <template
          v-else-if="
            upgradeStep === UpgradeStep.ConfirmUpgradePlatinumPlanMonthly ||
            upgradeStep === UpgradeStep.ConfirmUpgradePlatinumPlanAnnual
          "
        >
          <OzCloseButton :disabled="isProcessingUpgrade" @click="$emit('cancel')" />
          <DashboardSettingsBillingModalTitle :title="__('Switch to Platinum')" :subtitle="__('Confirm changes')" />
          <div
            v-if="upgradeError"
            :class="[
              'mx-0 tablet-portrait:mx-4 mb-4 tablet-portrait:mb-6',
              'py-7',
              'flex flex-col items-center',
              'text-dark-text-100 dark:text-light-text-100',
            ]"
          >
            {{ upgradeError }}
          </div>
          <div v-else class="mx-0 tablet-portrait:mx-2">
            <!-- Show Platinum monthly payment schedule information -->
            <BillingCollectionItems
              v-if="upgradeStep === UpgradeStep.ConfirmUpgradePlatinumPlanMonthly"
              class="mb-4 tablet-portrait:mb-3"
              :items="[
                __('You are switching to <strong>Platinum</strong> starting now (<strong>%{currentDate}</strong>).', {
                  currentDate,
                }),
                __('Your recurring charges will be <strong>%{amount}</strong> a month.', {
                  amount: displayedPrices.platinum.monthly,
                }),
                __('You can cancel or change your membership anytime.'),
              ]"
            />
            <!-- Show Platinum annual payment schedule information -->
            <BillingCollectionItems
              v-if="upgradeStep === UpgradeStep.ConfirmUpgradePlatinumPlanAnnual"
              class="mb-4 tablet-portrait:mb-3"
              :items="[
                __('You are switching to <strong>Platinum</strong> starting now (<strong>%{currentDate}</strong>).', {
                  currentDate,
                }),
                __('Your recurring charges will be <strong>%{amount}</strong> a year.', {
                  amount: displayedPrices.platinum.annual,
                }),
                __('You can cancel or change your membership anytime.'),
              ]"
            />
            <BillingCollectionItems
              v-if="displayedEstimatedUpgradeCost.is_refund"
              :items="[
                __(
                  'The unused portion of your current payment period amounts to <strong>%{amountUnused}</strong>. We will subtract <strong>%{amount}</strong> for your first monthly payment.',
                  {
                    amountUnused: displayedEstimatedUpgradeCost.unused_portion,
                    amount: displayedEstimatedUpgradeCost.invoice_amount,
                  },
                ),
                __('We will issue a <strong>%{refundAmount}</strong> refund to your card now.', {
                  refundAmount: displayedEstimatedUpgradeCost.refund_amount,
                }),
              ]"
            />
            <BillingCollectionItems
              v-else
              :items="[
                __(
                  'The unused portion of your current payment period amounts to <strong>%{amountUnused}</strong>. We will apply it as a credit towards your first invoice.',
                  {
                    amountUnused: displayedEstimatedUpgradeCost.unused_portion,
                  },
                ),
                __('We will charge your card <strong>%{amount}</strong> now.', {
                  amount: displayedEstimatedUpgradeCost.amount_due,
                }),
              ]"
            />
            <div class="my-4 tablet-portrait:mt-6 tablet-portrait:mb-2 flex justify-end">
              <MembershipTierActionButton
                class="w-full tablet-portrait:w-auto"
                :button-text="__('Confirm')"
                :processing-text="__('Switching...')"
                :is-processing="isProcessingUpgrade"
                @click="confirmUpgrade(upgradeToPlatinum)"
              />
            </div>
          </div>
        </template>
        <template
          v-else-if="
            upgradeStep === UpgradeStep.ConfirmUpgradePlatinumPlanDifferentCurrencyMonthly ||
            upgradeStep === UpgradeStep.ConfirmUpgradePlatinumPlanDifferentCurrencyAnnual
          "
        >
          <OzCloseButton
            :disabled="isProcessingUpgrade || isLoadingCancellationRefundInformation"
            @click="$emit('cancel')"
          />
          <DashboardSettingsBillingModalTitle :title="__('Switch to Platinum')" :subtitle="__('Confirm changes')" />
          <div
            v-if="upgradeError"
            :class="[
              'mx-0 tablet-portrait:mx-4 mb-4 tablet-portrait:mb-6',
              'py-7',
              'flex flex-col items-center',
              'text-dark-text-100 dark:text-light-text-100',
            ]"
          >
            {{ upgradeError }}
          </div>
          <div v-else-if="isLoadingCancellationRefundInformation" class="flex justify-center py-7">
            <OzIndeterminateSpinner />
          </div>
          <div v-else class="mx-0 tablet-portrait:mx-2">
            <!-- Show Platinum monthly payment schedule information -->
            <template v-if="upgradeStep === UpgradeStep.ConfirmUpgradePlatinumPlanDifferentCurrencyMonthly">
              <BillingCollectionItems
                class="mb-4 tablet-portrait:mb-3"
                :items="[
                  __('You are switching to <strong>Platinum</strong> starting now (<strong>%{currentDate}</strong>).', {
                    currentDate,
                  }),
                  __('Your recurring charges will be <strong>%{amount}</strong> a month.', {
                    amount: displayedPrices.platinum.monthly,
                  }),
                  __('You can cancel or change your membership anytime.'),
                ]"
              />
              <BillingCollectionItems
                :items="[
                  __(
                    'The unused portion of your current payment period amounts to <strong>%{refundAmount}</strong>. We will refund it to your card now.',
                    {
                      refundAmount: estimatedCancellationRefundAmount,
                    },
                  ),
                  __('We will charge your card <strong>%{amount}</strong> now.', {
                    amount: displayedPrices.platinum.monthly,
                  }),
                ]"
              />
            </template>
            <!-- Show Platinum annual payment schedule information -->
            <template v-if="upgradeStep === UpgradeStep.ConfirmUpgradePlatinumPlanDifferentCurrencyAnnual">
              <BillingCollectionItems
                class="mb-4 tablet-portrait:mb-3"
                :items="[
                  __('You are switching to <strong>Platinum</strong> starting now (<strong>%{currentDate}</strong>).', {
                    currentDate,
                  }),
                  __('Your recurring charges will be <strong>%{amount}</strong> a year.', {
                    amount: displayedPrices.platinum.annual,
                  }),
                  __('You can cancel or change your membership anytime.'),
                ]"
              />
              <BillingCollectionItems
                :items="[
                  __(
                    'The unused portion of your current payment period amounts to <strong>%{refundAmount}</strong>. We will refund it to your card now.',
                    {
                      refundAmount: estimatedCancellationRefundAmount,
                    },
                  ),
                  __('We will charge your card <strong>%{amount}</strong> now.', {
                    amount: displayedPrices.platinum.annual,
                  }),
                ]"
              />
            </template>
            <div class="my-4 tablet-portrait:mt-6 tablet-portrait:mb-2 flex justify-end">
              <MembershipTierActionButton
                class="w-full tablet-portrait:w-auto"
                :button-text="__('Confirm')"
                :processing-text="__('Switching...')"
                :is-processing="isProcessingUpgrade"
                @click="confirmUpgrade(upgradeToPlatinumDifferentCurrency)"
              />
            </div>
          </div>
        </template>
      </div>
    </template>
  </OzScreenSizeAwareCenterModal>
</template>

<style lang="scss" scoped>
:deep(.modal-container) {
  height: auto !important;
  box-shadow: none !important;
  // ensure modal height is set based on content height
}
</style>
